import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, SlidersHorizontal, X } from "lucide-react";
import { fileUrl, formatArea, formatPriceHuf } from "@/lib/format";
import { unitDisplayPriceHuf } from "@shared/pricing";
import { RequestQuoteInline, unitQuoteHref } from "@/components/request-quote";
import {
  UNIT_STATUS_BADGE,
  UNIT_STATUS_LABEL,
  unitSlug,
} from "@/lib/unit-helpers";
import type { ProjectUnit } from "@shared/schema";

type UnitFilterProps = {
  units: ProjectUnit[];
  projectSlug: string;
  /** Ár-elrejtés (projekt-szintű): a „Max. vételár" csúszka eltűnik, a
   *  találatoknál pedig az ár helyén „Kérjen ajánlatot" áll. */
  pricesHidden?: boolean;
};

/**
 * Calculator-style live filter for a project's apartment list.
 * Slide / toggle the criteria and the matching unit cards update
 * in real time underneath. Big "wow" UX for the sales pitch.
 */
export function UnitFilter({
  units,
  projectSlug,
  pricesHidden = false,
}: UnitFilterProps) {
  // Derive defaults from the actual unit set so sliders span sensible ranges
  const priceBounds = useMemo(() => {
    const prices = units
      .map((u) => unitDisplayPriceHuf(u) ?? 0)
      .filter((p) => p > 0);
    if (prices.length === 0) return { min: 30_000_000, max: 100_000_000 };
    return {
      min: Math.floor(Math.min(...prices) / 1_000_000) * 1_000_000,
      max: Math.ceil(Math.max(...prices) / 1_000_000) * 1_000_000,
    };
  }, [units]);

  const areaBounds = useMemo(() => {
    const areas = units
      .map((u) => Number(u.areaSqm))
      .filter((a) => !Number.isNaN(a) && a > 0);
    if (areas.length === 0) return { min: 30, max: 120 };
    return {
      min: Math.floor(Math.min(...areas)),
      max: Math.ceil(Math.max(...areas)),
    };
  }, [units]);

  const allRooms = useMemo(
    () => Array.from(new Set(units.map((u) => u.rooms ?? "—"))).sort(),
    [units],
  );

  const [rooms, setRooms] = useState<string>("all");
  const [maxPrice, setMaxPrice] = useState(priceBounds.max);
  const [minArea, setMinArea] = useState(areaBounds.min);
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [needsBalcony, setNeedsBalcony] = useState(false);
  const [needsParking, setNeedsParking] = useState(false);

  const filtered = useMemo(() => {
    return units.filter((u) => {
      if (rooms !== "all" && u.rooms !== rooms) return false;
      if (!pricesHidden) {
        const price = unitDisplayPriceHuf(u);
        if (price != null && price > maxPrice) return false;
      }
      const area = Number(u.areaSqm);
      if (!Number.isNaN(area) && area < minArea) return false;
      if (onlyAvailable && u.status !== "available") return false;
      if (needsBalcony) {
        const bal = Number(u.balconyAreaSqm);
        const ter = Number(u.terraceAreaSqm);
        const garden = Number(u.gardenAreaSqm);
        const hasOutdoor =
          (!Number.isNaN(bal) && bal > 0) ||
          (!Number.isNaN(ter) && ter > 0) ||
          (!Number.isNaN(garden) && garden > 0);
        if (!hasOutdoor) return false;
      }
      if (needsParking && !u.parkingIncluded) return false;
      return true;
    });
  }, [
    units,
    rooms,
    maxPrice,
    minArea,
    onlyAvailable,
    needsBalcony,
    needsParking,
    pricesHidden,
  ]);

  function resetAll() {
    setRooms("all");
    setMaxPrice(priceBounds.max);
    setMinArea(areaBounds.min);
    setOnlyAvailable(true);
    setNeedsBalcony(false);
    setNeedsParking(false);
  }

  const matchCount = filtered.length;
  const totalCount = units.length;

  return (
    <section className="bg-brand-50 border border-brand-100 p-6 lg:p-8 max-w-full overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-12 h-12 grid place-items-center bg-accent text-white shrink-0">
            <SlidersHorizontal size={22} />
          </div>
          <div className="min-w-0">
            {/* tracking-wider on mobile keeps Hungarian titles like
                "TÖKÉLETES LAKÁST" inside narrow viewports without
                horizontal overflow. */}
            <h2 className="text-base sm:text-lg font-semibold uppercase tracking-wider sm:tracking-widish text-brand-700 break-words">
              Találd meg a tökéletes lakást
            </h2>
            <p className="text-xs text-brand-500 mt-0.5">
              Állítsa be a paramétereket — a találatok élőben frissülnek.
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-3xl text-accent leading-none">
            {String(matchCount).padStart(2, "0")}
          </div>
          <div className="text-[10px] uppercase tracking-widish text-brand-500 font-semibold mt-1">
            találat / {totalCount}
          </div>
        </div>
      </div>

      {/* Filter controls */}
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
        {/* Rooms */}
        <div>
          <label className="text-xs uppercase tracking-widish font-semibold text-brand-500 mb-2 block">
            Szobaszám
          </label>
          <div className="flex flex-wrap gap-2">
            <RoomChip
              label="Mindegy"
              active={rooms === "all"}
              onClick={() => setRooms("all")}
            />
            {allRooms.map((r) => (
              <RoomChip
                key={r}
                label={r}
                active={rooms === r}
                onClick={() => setRooms(r)}
              />
            ))}
          </div>
        </div>

        {/* Max price — ár-elrejtésnél nincs értelme (nincs publikus ár) */}
        {!pricesHidden && (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-xs uppercase tracking-widish font-semibold text-brand-500">
              Max. vételár
            </label>
            <span className="text-sm font-semibold text-brand-700">
              {formatPriceHuf(maxPrice)}
            </span>
          </div>
          <input
            type="range"
            min={priceBounds.min}
            max={priceBounds.max}
            step={500_000}
            value={maxPrice}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
            className="calc-slider"
          />
        </div>
        )}

        {/* Min area */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-xs uppercase tracking-widish font-semibold text-brand-500">
              Min. alapterület
            </label>
            <span className="text-sm font-semibold text-brand-700">
              {minArea} m²
            </span>
          </div>
          <input
            type="range"
            min={areaBounds.min}
            max={areaBounds.max}
            step={1}
            value={minArea}
            onChange={(e) => setMinArea(Number(e.target.value))}
            className="calc-slider"
          />
        </div>

        {/* Checkboxes */}
        <div>
          <label className="text-xs uppercase tracking-widish font-semibold text-brand-500 mb-2 block">
            További szűrők
          </label>
          <div className="flex flex-col gap-2">
            <FilterToggle
              label="Csak szabad lakások"
              checked={onlyAvailable}
              onChange={setOnlyAvailable}
            />
            <FilterToggle
              label="Erkély / terasz / kert"
              checked={needsBalcony}
              onChange={setNeedsBalcony}
            />
            <FilterToggle
              label="Parkolóhely jár"
              checked={needsParking}
              onChange={setNeedsParking}
            />
          </div>
        </div>
      </div>

      {(rooms !== "all" ||
        maxPrice !== priceBounds.max ||
        minArea !== areaBounds.min ||
        !onlyAvailable ||
        needsBalcony ||
        needsParking) && (
        <button
          type="button"
          onClick={resetAll}
          className="mt-4 inline-flex items-center gap-1 text-xs text-brand-500 hover:text-accent uppercase tracking-widish font-semibold"
        >
          <X size={12} /> Szűrők törlése
        </button>
      )}

      {/* Match list */}
      <div className="mt-6 pt-6 border-t border-brand-200">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-brand-500 text-sm">
            Nincs a feltételeknek megfelelő lakás. Próbálja kevesebb
            szűrővel — vagy{" "}
            <a
              href="/kapcsolat"
              className="text-accent underline hover:text-accent-700"
            >
              kérdezze meg kollégánkat
            </a>
            .
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((u) => (
              <li key={u.id}>
                <Link
                  href={`/projektek/${projectSlug}/${unitSlug(u)}`}
                  className="flex items-center gap-4 bg-white border border-brand-100 hover:border-accent transition-colors px-4 py-3 group"
                >
                  {u.gallery[0] && (
                    <div className="w-14 h-14 overflow-hidden shrink-0 bg-brand-100">
                      <img
                        src={fileUrl(u.gallery[0].key)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-heading text-base uppercase tracking-widish text-brand-700 group-hover:text-accent transition-colors">
                        {u.label}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-widish font-semibold px-2 py-0.5 ${UNIT_STATUS_BADGE[u.status]}`}
                      >
                        {UNIT_STATUS_LABEL[u.status]}
                      </span>
                    </div>
                    <div className="text-xs text-brand-500 mt-0.5">
                      {u.rooms} szoba ·{" "}
                      {formatArea(u.areaSqm as unknown as number | null)}
                      {u.floor != null && ` · ${u.floor}. emelet`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-medium text-sm text-brand-700">
                      {pricesHidden ? (
                        <RequestQuoteInline
                          href={unitQuoteHref(projectSlug, u)}
                        />
                      ) : (
                        formatPriceHuf(unitDisplayPriceHuf(u))
                      )}
                    </div>
                    <div className="text-[10px] text-accent uppercase tracking-widish font-semibold inline-flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Megnézem <ArrowRight size={10} />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function RoomChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs uppercase tracking-widish font-semibold border transition-colors ${
        active
          ? "bg-accent text-white border-accent"
          : "bg-white text-brand-700 border-brand-200 hover:border-brand-400"
      }`}
    >
      {label}
    </button>
  );
}

function FilterToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-brand-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-accent cursor-pointer"
      />
      <span>{label}</span>
    </label>
  );
}
